import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProgress } from "@/hooks/useUserProgress";
import { useUserCohort } from "@/hooks/cohort/useUserCohort";
import { useTransactions } from "@/hooks/useTransactions";
import {
  SnapshotService,
  TransactionSnapshot,
  filterSnapshotsToTransactions,
} from "@/services/SnapshotService";
import { getCurrencySymbol } from "@/utils/currencies";
import { getTrainerActiveCohort } from "@/utils/session";
import { ROLES } from "@/constants/roles";
import { openPrintableDocument } from "@/utils/exportDocument";

/**
 * Self-contained export of the Transaction Summary as a standalone HTML report
 * opened in a new window. Extracted from the (now hidden) Transaction Summary
 * page so the export can be triggered from the top navigation on any page that
 * renders the user header.
 */
export const useTransactionSummaryExport = () => {
  const { currentUser } = useAuth();
  const trainerCohortId =
    currentUser?.role === ROLES.TRAINER
      ? getTrainerActiveCohort() ?? undefined
      : undefined;
  const { cohort } = useUserCohort(trainerCohortId);
  const { transactions: configuredTransactions } =
    useTransactions(trainerCohortId);

  const {
    companyName,
    accounts: liveAccounts,
    liabilities: liveLiabilities,
    equityAccounts: liveEquityAccounts,
    expenseAccounts: liveExpenseAccounts,
    incomeAccounts: liveIncomeAccounts,
    accountDroppedAmounts: liveAccountDroppedAmounts,
    liabilityDroppedAmounts: liveLiabilityDroppedAmounts,
    equityAccountDroppedAmounts: liveEquityAccountDroppedAmounts,
    expenseDroppedAmounts: liveExpenseDroppedAmounts,
    incomeDroppedAmounts: liveIncomeDroppedAmounts,
    getTransactionNote,
    sectionHints,
    viewMode,
  } = useUserProgress(trainerCohortId);

  const [loadedSnapshots, setLoadedSnapshots] = useState<
    TransactionSnapshot[]
  >([]);
  const [isExporting, setIsExporting] = useState(false);

  // Ignore snapshots for transactions that were later deleted from the cohort.
  const snapshots = useMemo(
    () =>
      filterSnapshotsToTransactions(
        loadedSnapshots,
        (configuredTransactions || []).map((t) => t.id)
      ),
    [loadedSnapshots, configuredTransactions]
  );

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.id || !cohort?.id) return;
      try {
        const list = await SnapshotService.getAllSnapshotsForUserCohort({
          userId: currentUser.id,
          cohortId: cohort.id,
          viewMode,
        });
        setLoadedSnapshots(list);
      } catch (err) {
        console.error("Failed to load snapshots for export", err);
        setLoadedSnapshots([]);
      }
    };
    load();
  }, [currentUser?.id, cohort?.id, viewMode]);

  const currencySymbol = getCurrencySymbol(currentUser?.currency);

  const formatCurrency = (amount: number) => {
    return `${currencySymbol}${amount.toLocaleString()}`;
  };

  const formatTransactionDate = (dateStr: string) => {
    if (!dateStr) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = dateStr.split("/");
    if (parts.length === 2) {
      const day = parts[0].padStart(2, "0");
      const monthIndex = parseInt(parts[1], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${day} ${months[monthIndex]}`;
      }
    }
    return dateStr;
  };

  const defaultSectionHints: Record<string, string> = {
    assets: "Own",
    liabilities: "Owe",
    equity: "Owners' share",
    expenses: "Usage",
    income: "Service rendered",
  };
  const getSectionHint = (type: string) =>
    (sectionHints && sectionHints[type]) || defaultSectionHints[type] || "";

  const calculateBalance = (
    accountId: string,
    droppedAmounts: Record<string, any[]>,
    accountType?: "assets" | "liabilities" | "equity" | "expenses" | "income",
  ) => {
    const amounts = droppedAmounts[accountId] || [];
    return amounts.reduce((sum, amt) => {
      const isPositive = amt.isPositive;
      const amount = amt.amount || 0;
      if (
        accountType === "liabilities" ||
        accountType === "equity" ||
        accountType === "income"
      ) {
        return sum + (isPositive ? -Math.abs(amount) : Math.abs(amount));
      }
      return sum + (isPositive ? Math.abs(amount) : -Math.abs(amount));
    }, 0);
  };

  const buildLiveData = useCallback(() => {
    const buildAccountsWithBalances = (
      accountDefs: any[],
      droppedAmountsMap: Record<string, any[]>,
      accountType: "assets" | "liabilities" | "equity" | "expenses" | "income",
    ) => {
      const result: any[] = [];
      accountDefs.forEach((acc) => {
        const amounts = droppedAmountsMap[acc.id] || [];
        const amountsByTx: Record<number, any[]> = {};
        amounts.forEach((amt: any) => {
          const txId = amt.transactionId || 1;
          if (!amountsByTx[txId]) amountsByTx[txId] = [];
          amountsByTx[txId].push(amt);
        });
        Object.entries(amountsByTx).forEach(([txIdStr, txAmounts]) => {
          const txId = parseInt(txIdStr, 10);
          const balance = calculateBalance(
            acc.id,
            { [acc.id]: txAmounts },
            accountType,
          );
          if (balance !== 0) {
            result.push({
              id: acc.id,
              name: acc.name || "",
              balance: balance,
              transactionIndex: txId,
              label: acc.label,
              isPositive: txAmounts.length > 0 ? txAmounts[0].isPositive : true,
            });
          }
        });
      });
      return result;
    };

    return {
      assets: buildAccountsWithBalances(
        liveAccounts || [],
        liveAccountDroppedAmounts || {},
        "assets",
      ),
      expenses: buildAccountsWithBalances(
        liveExpenseAccounts || [],
        liveExpenseDroppedAmounts || {},
        "expenses",
      ),
      liabilities: buildAccountsWithBalances(
        liveLiabilities || [],
        liveLiabilityDroppedAmounts || {},
        "liabilities",
      ),
      equity: buildAccountsWithBalances(
        liveEquityAccounts || [],
        liveEquityAccountDroppedAmounts || {},
        "equity",
      ),
      income: buildAccountsWithBalances(
        liveIncomeAccounts || [],
        liveIncomeDroppedAmounts || {},
        "income",
      ),
    };
  }, [
    liveAccounts,
    liveLiabilities,
    liveEquityAccounts,
    liveExpenseAccounts,
    liveIncomeAccounts,
    liveAccountDroppedAmounts,
    liveLiabilityDroppedAmounts,
    liveEquityAccountDroppedAmounts,
    liveExpenseDroppedAmounts,
    liveIncomeDroppedAmounts,
  ]);

  // Per-amount view used for the Activity Area Snapshot cards. Unlike
  // buildLiveData (which nets amounts per transaction and drops net-zero
  // entries), this keeps every loaded amount as its own pill so accounts that
  // have amounts but net to zero still appear, mirroring the dashboard.
  const buildActivityDisplayData = useCallback(() => {
    const buildPerAmount = (
      accountDefs: any[],
      droppedAmountsMap: Record<string, any[]>,
      accountType: "assets" | "liabilities" | "equity" | "expenses" | "income",
    ) => {
      const result: any[] = [];
      accountDefs.forEach((acc) => {
        const amounts = droppedAmountsMap[acc.id] || [];
        amounts.forEach((amt: any) => {
          const balance = calculateBalance(
            acc.id,
            { [acc.id]: [amt] },
            accountType,
          );
          result.push({
            id: acc.id,
            name: acc.name || "",
            balance,
            transactionIndex: amt.transactionId || 1,
            label: acc.label,
            isPositive: amt.isPositive,
          });
        });
      });
      return result;
    };

    return {
      assets: buildPerAmount(
        liveAccounts || [],
        liveAccountDroppedAmounts || {},
        "assets",
      ),
      expenses: buildPerAmount(
        liveExpenseAccounts || [],
        liveExpenseDroppedAmounts || {},
        "expenses",
      ),
      liabilities: buildPerAmount(
        liveLiabilities || [],
        liveLiabilityDroppedAmounts || {},
        "liabilities",
      ),
      equity: buildPerAmount(
        liveEquityAccounts || [],
        liveEquityAccountDroppedAmounts || {},
        "equity",
      ),
      income: buildPerAmount(
        liveIncomeAccounts || [],
        liveIncomeDroppedAmounts || {},
        "income",
      ),
    };
  }, [
    liveAccounts,
    liveLiabilities,
    liveEquityAccounts,
    liveExpenseAccounts,
    liveIncomeAccounts,
    liveAccountDroppedAmounts,
    liveLiabilityDroppedAmounts,
    liveEquityAccountDroppedAmounts,
    liveExpenseDroppedAmounts,
    liveIncomeDroppedAmounts,
  ]);

  const buildAccountMetadataLookup = (snapshots: any[], upToIndex: number) => {
    const lookup: Record<
      string,
      Record<string, { name: string; label: string }>
    > = {
      assets: {},
      liabilities: {},
      equity: {},
      expenses: {},
      income: {},
    };

    (liveAccounts || []).forEach((account: any) => {
      if (account.id) {
        lookup.assets[account.id] = {
          name: account.name || "",
          label: account.label || "",
        };
      }
    });
    (liveLiabilities || []).forEach((account: any) => {
      if (account.id) {
        lookup.liabilities[account.id] = {
          name: account.name || "",
          label: account.label || "",
        };
      }
    });
    (liveEquityAccounts || []).forEach((account: any) => {
      if (account.id) {
        lookup.equity[account.id] = {
          name: account.name || "",
          label: account.label || "",
        };
      }
    });
    (liveExpenseAccounts || []).forEach((account: any) => {
      if (account.id) {
        lookup.expenses[account.id] = {
          name: account.name || "",
          label: account.label || "",
        };
      }
    });
    (liveIncomeAccounts || []).forEach((account: any) => {
      if (account.id) {
        lookup.income[account.id] = {
          name: account.name || "",
          label: account.label || "",
        };
      }
    });

    snapshots.slice(0, upToIndex + 1).forEach((snapshot) => {
      const accounts = (snapshot?.payload as any)?.accounts || {};
      (
        ["assets", "liabilities", "equity", "expenses", "income"] as const
      ).forEach((type) => {
        const accountKey =
          type === "assets"
            ? "assets"
            : type === "liabilities"
              ? "liabilities"
              : type === "equity"
                ? "equity"
                : type === "expenses"
                  ? "expenses"
                  : "income";

        (accounts[accountKey] || []).forEach((account: any) => {
          const existing = lookup[type][account.id] || { name: "", label: "" };
          const newName =
            account.name && account.name.trim() !== ""
              ? account.name
              : existing.name;
          const newLabel =
            account.label && account.label !== ""
              ? account.label
              : existing.label;
          lookup[type][account.id] = { name: newName, label: newLabel };
        });
      });
    });

    return lookup;
  };

  const getConsolidatedDataForCurrentView = (
    snapshots: any[],
    upToIndex: number,
  ) => {
    const assets: Array<{
      id: string;
      name: string;
      label: string;
      balance: number;
      transactionIndex: number;
      isPositive?: boolean;
    }> = [];
    const expenses: Array<{
      id: string;
      name: string;
      label: string;
      balance: number;
      transactionIndex: number;
      isPositive?: boolean;
    }> = [];
    const liabilities: Array<{
      id: string;
      name: string;
      label: string;
      balance: number;
      transactionIndex: number;
      isPositive?: boolean;
    }> = [];
    const equity: Array<{
      id: string;
      name: string;
      label: string;
      balance: number;
      transactionIndex: number;
      isPositive?: boolean;
    }> = [];
    const income: Array<{
      id: string;
      name: string;
      label: string;
      balance: number;
      transactionIndex: number;
      isPositive?: boolean;
    }> = [];

    const metadataLookup = buildAccountMetadataLookup(snapshots, upToIndex);
    const addedEntries = new Set<string>();

    for (let snapshotIndex = 0; snapshotIndex <= upToIndex; snapshotIndex++) {
      const snapshot = snapshots[snapshotIndex];
      if (!snapshot) continue;

      const accounts = (snapshot?.payload as any)?.accounts || {};
      const amounts = (snapshot?.payload as any)?.amounts || {};
      const snapshotTransactionId = snapshot.transactionId || snapshotIndex + 1;

      const filterAmountsForThisTransaction = (amountsArray: any[]) => {
        return (amountsArray || []).filter(
          (amt: any) => amt.transactionId === snapshotTransactionId,
        );
      };

      (accounts.assets || []).forEach((account: any) => {
        const txAmounts = filterAmountsForThisTransaction(
          amounts.accountDroppedAmounts?.[account.id],
        );
        if (txAmounts.length === 0) return;

        const entryKey = `assets-${account.id}-t${snapshotTransactionId}`;
        if (addedEntries.has(entryKey)) return;
        addedEntries.add(entryKey);

        const balance = calculateBalance(
          account.id,
          { [account.id]: txAmounts },
          "assets",
        );
        if (balance !== 0) {
          const metadata = metadataLookup.assets[account.id] || {
            name: "",
            label: "",
          };
          const accountName =
            account.name && account.name.trim() !== ""
              ? account.name
              : metadata.name;
          const accountLabel =
            account.label && account.label !== ""
              ? account.label
              : metadata.label;
          const isPositiveFromData =
            txAmounts.length > 0 ? txAmounts[0].isPositive : true;

          assets.push({
            id: `${account.id}-t${snapshotTransactionId}`,
            name: accountName,
            label: accountLabel,
            balance: balance,
            transactionIndex: snapshotTransactionId,
            isPositive: isPositiveFromData,
          });
        }
      });

      (accounts.expenses || []).forEach((account: any) => {
        const txAmounts = filterAmountsForThisTransaction(
          amounts.expenseDroppedAmounts?.[account.id],
        );
        if (txAmounts.length === 0) return;

        const entryKey = `expenses-${account.id}-t${snapshotTransactionId}`;
        if (addedEntries.has(entryKey)) return;
        addedEntries.add(entryKey);

        const balance = calculateBalance(
          account.id,
          { [account.id]: txAmounts },
          "expenses",
        );
        if (balance !== 0) {
          const metadata = metadataLookup.expenses[account.id] || {
            name: "",
            label: "",
          };
          const accountName =
            account.name && account.name.trim() !== ""
              ? account.name
              : metadata.name;
          const accountLabel =
            account.label && account.label !== ""
              ? account.label
              : metadata.label;
          const isPositiveFromData =
            txAmounts.length > 0 ? txAmounts[0].isPositive : true;

          expenses.push({
            id: `${account.id}-t${snapshotTransactionId}`,
            name: accountName,
            label: accountLabel,
            balance: balance,
            transactionIndex: snapshotTransactionId,
            isPositive: isPositiveFromData,
          });
        }
      });

      (accounts.liabilities || []).forEach((account: any) => {
        const txAmounts = filterAmountsForThisTransaction(
          amounts.liabilityDroppedAmounts?.[account.id],
        );
        if (txAmounts.length === 0) return;

        const entryKey = `liabilities-${account.id}-t${snapshotTransactionId}`;
        if (addedEntries.has(entryKey)) return;
        addedEntries.add(entryKey);

        const balance = calculateBalance(
          account.id,
          { [account.id]: txAmounts },
          "liabilities",
        );
        if (balance !== 0) {
          const metadata = metadataLookup.liabilities[account.id] || {
            name: "",
            label: "",
          };
          const accountName =
            account.name && account.name.trim() !== ""
              ? account.name
              : metadata.name;
          const accountLabel =
            account.label && account.label !== ""
              ? account.label
              : metadata.label;
          const isPositiveFromData =
            txAmounts.length > 0 ? txAmounts[0].isPositive : true;

          liabilities.push({
            id: `${account.id}-t${snapshotTransactionId}`,
            name: accountName,
            label: accountLabel,
            balance: balance,
            transactionIndex: snapshotTransactionId,
            isPositive: isPositiveFromData,
          });
        }
      });

      (accounts.equity || []).forEach((account: any) => {
        const txAmounts = filterAmountsForThisTransaction(
          amounts.equityAccountDroppedAmounts?.[account.id],
        );
        if (txAmounts.length === 0) return;

        const entryKey = `equity-${account.id}-t${snapshotTransactionId}`;
        if (addedEntries.has(entryKey)) return;
        addedEntries.add(entryKey);

        const balance = calculateBalance(
          account.id,
          { [account.id]: txAmounts },
          "equity",
        );
        if (balance !== 0) {
          const metadata = metadataLookup.equity[account.id] || {
            name: "",
            label: "",
          };
          const accountName =
            account.name && account.name.trim() !== ""
              ? account.name
              : metadata.name;
          const accountLabel =
            account.label && account.label !== ""
              ? account.label
              : metadata.label;
          const isPositiveFromData =
            txAmounts.length > 0 ? txAmounts[0].isPositive : true;

          equity.push({
            id: `${account.id}-t${snapshotTransactionId}`,
            name: accountName,
            label: accountLabel,
            balance: balance,
            transactionIndex: snapshotTransactionId,
            isPositive: isPositiveFromData,
          });
        }
      });

      (accounts.income || []).forEach((account: any) => {
        const txAmounts = filterAmountsForThisTransaction(
          amounts.incomeDroppedAmounts?.[account.id],
        );
        if (txAmounts.length === 0) return;

        const entryKey = `income-${account.id}-t${snapshotTransactionId}`;
        if (addedEntries.has(entryKey)) return;
        addedEntries.add(entryKey);

        const balance = calculateBalance(
          account.id,
          { [account.id]: txAmounts },
          "income",
        );
        if (balance !== 0) {
          const metadata = metadataLookup.income[account.id] || {
            name: "",
            label: "",
          };
          const accountName =
            account.name && account.name.trim() !== ""
              ? account.name
              : metadata.name;
          const accountLabel =
            account.label && account.label !== ""
              ? account.label
              : metadata.label;
          const isPositiveFromData =
            txAmounts.length > 0 ? txAmounts[0].isPositive : true;

          income.push({
            id: `${account.id}-t${snapshotTransactionId}`,
            name: accountName,
            label: accountLabel,
            balance: balance,
            transactionIndex: snapshotTransactionId,
            isPositive: isPositiveFromData,
          });
        }
      });
    }

    return { assets, liabilities, equity, income, expenses };
  };

  const buildAllJournalEntries = () => {
    type JournalEntry = {
      transactionId: number;
      date: string;
      details: string;
      notes?: string;
      accounts: Array<{
        name: string;
        type: "asset" | "liability" | "equity" | "expense" | "income";
        debit: number | null;
        credit: number | null;
      }>;
    };

    const transactionMap = new Map<number, JournalEntry>();

    const exportLiveData = buildLiveData();
    const hasExportLiveData =
      exportLiveData.assets.length > 0 ||
      exportLiveData.expenses.length > 0 ||
      exportLiveData.liabilities.length > 0 ||
      exportLiveData.equity.length > 0 ||
      exportLiveData.income.length > 0;
    const allData = hasExportLiveData
      ? exportLiveData
      : getConsolidatedDataForCurrentView(snapshots, snapshots.length - 1);

    // Date/details for a transaction, from the cohort's configured list. This
    // is the fallback when no snapshot exists for it.
    const txMeta = new Map<number, { date: string; details: string }>();
    (configuredTransactions || []).forEach((tx: any, i: number) => {
      const id = Number(tx?.id ?? i + 1);
      if (!isNaN(id)) {
        txMeta.set(id, { date: tx?.date || "", details: tx?.details || "" });
      }
    });

    snapshots.forEach((snapshot, idx) => {
      const txId = snapshot.transactionId || idx + 1;
      const txPayload = snapshot?.payload?.transaction as any;

      if (!transactionMap.has(txId)) {
        transactionMap.set(txId, {
          transactionId: txId,
          date: txPayload?.date || txMeta.get(txId)?.date || "",
          details: txPayload?.details || txMeta.get(txId)?.details || "",
          notes: txPayload?.notes || getTransactionNote(txId) || "",
          accounts: [],
        });
      }
    });

    // Snapshots are written per transaction and may be missing entirely (they
    // load asynchronously, and older progress predates them). The account data
    // itself carries the transaction it belongs to, so create any entry the
    // snapshots didn't cover — otherwise the Journal Entries table comes out
    // empty even though there are amounts to report.
    const entryFor = (txId: number) => {
      const existing = transactionMap.get(txId);
      if (existing) return existing;
      const meta = txMeta.get(txId);
      const created: JournalEntry = {
        transactionId: txId,
        date: meta?.date || "",
        details: meta?.details || "",
        notes: getTransactionNote(txId) || "",
        accounts: [],
      };
      transactionMap.set(txId, created);
      return created;
    };

    allData.assets.forEach((account) => {
      const entry = entryFor(account.transactionIndex);
      {
        const amount = Math.abs(account.balance);
        const isDebit = account.isPositive === true;
        entry.accounts.push({
          name:
            account.name && account.name.trim() !== "" ? account.name : "Asset",
          type: "asset",
          debit: isDebit ? amount : null,
          credit: !isDebit ? amount : null,
        });
      }
    });

    allData.expenses.forEach((account) => {
      const entry = entryFor(account.transactionIndex);
      {
        const amount = Math.abs(account.balance);
        const isDebit = account.isPositive === true;
        entry.accounts.push({
          name:
            account.name && account.name.trim() !== ""
              ? account.name
              : "Expense",
          type: "expense",
          debit: isDebit ? amount : null,
          credit: !isDebit ? amount : null,
        });
      }
    });

    allData.liabilities.forEach((account) => {
      const entry = entryFor(account.transactionIndex);
      {
        const amount = Math.abs(account.balance);
        const isDebit = account.isPositive === true;
        entry.accounts.push({
          name:
            account.name && account.name.trim() !== ""
              ? account.name
              : "Liability",
          type: "liability",
          debit: isDebit ? amount : null,
          credit: !isDebit ? amount : null,
        });
      }
    });

    allData.equity.forEach((account) => {
      const entry = entryFor(account.transactionIndex);
      {
        const amount = Math.abs(account.balance);
        const isDebit = account.isPositive === true;
        entry.accounts.push({
          name:
            account.name && account.name.trim() !== ""
              ? account.name
              : "Equity",
          type: "equity",
          debit: isDebit ? amount : null,
          credit: !isDebit ? amount : null,
        });
      }
    });

    allData.income.forEach((account) => {
      const entry = entryFor(account.transactionIndex);
      {
        const amount = Math.abs(account.balance);
        const isDebit = account.isPositive === true;
        entry.accounts.push({
          name:
            account.name && account.name.trim() !== ""
              ? account.name
              : "Income",
          type: "income",
          debit: isDebit ? amount : null,
          credit: !isDebit ? amount : null,
        });
      }
    });

    // Journal convention: the debit (blue) lines of a transaction are always
    // listed before its credit (red) lines. Accounts are collected in section
    // order (assets, expenses, liabilities, …), so without this a credit-side
    // asset would sit above a debit-side liability. Sort is stable, so the
    // section order is preserved within the debit and credit groups.
    transactionMap.forEach((entry) => {
      entry.accounts.sort(
        (a, b) => (a.debit != null ? 0 : 1) - (b.debit != null ? 0 : 1),
      );
    });

    return Array.from(transactionMap.values())
      .filter((entry) => entry.accounts.length > 0)
      // Live dropped amounts may still reference a transaction that was later
      // deleted from the cohort — leave those out of the journal.
      .filter((entry) => txMeta.size === 0 || txMeta.has(entry.transactionId))
      .sort((a, b) => a.transactionId - b.transactionId);
  };

  const exportSummary = () => {
    setIsExporting(true);
    try {
      const totalTransactions = snapshots.length;
      const lastTxId =
        snapshots.length > 0
          ? snapshots[snapshots.length - 1]?.transactionId || totalTransactions
          : 0;

      const liveData = buildLiveData();
      const hasLiveData =
        liveData.assets.length > 0 ||
        liveData.expenses.length > 0 ||
        liveData.liabilities.length > 0 ||
        liveData.equity.length > 0 ||
        liveData.income.length > 0;
      const allData = hasLiveData
        ? liveData
        : getConsolidatedDataForCurrentView(snapshots, snapshots.length - 1);
      const allJournalEntries = buildAllJournalEntries();

      // Activity Area cards use per-amount data so accounts with loaded amounts
      // still show (with each amount as a pill) even when their net is zero.
      const liveDisplay = buildActivityDisplayData();
      const hasLiveDisplay =
        liveDisplay.assets.length > 0 ||
        liveDisplay.expenses.length > 0 ||
        liveDisplay.liabilities.length > 0 ||
        liveDisplay.equity.length > 0 ||
        liveDisplay.income.length > 0;
      const displayData = hasLiveDisplay ? liveDisplay : allData;

      const totalAssets = allData.assets.reduce((sum, a) => sum + a.balance, 0);
      const totalLiabilities = allData.liabilities.reduce(
        (sum, a) => sum + a.balance,
        0,
      );
      const totalEquity = allData.equity.reduce((sum, a) => sum + a.balance, 0);
      const totalExpenses = allData.expenses.reduce(
        (sum, a) => sum + a.balance,
        0,
      );
      const totalIncome = allData.income.reduce((sum, a) => sum + a.balance, 0);

      const groupAccounts = (
        accounts: Array<{
          name: string;
          balance: number;
          transactionIndex: number;
          label?: string;
          isPositive?: boolean;
        }>,
      ) => {
        const grouped = new Map<
          string,
          {
            name: string;
            label?: string;
            total: number;
            amounts: Array<{
              balance: number;
              txIndex: number;
              isPositive?: boolean;
            }>;
          }
        >();
        accounts.forEach((acc) => {
          const name = acc.name && acc.name.trim() !== "" ? acc.name : "";
          if (!grouped.has(name)) {
            grouped.set(name, {
              name,
              label: acc.label,
              total: 0,
              amounts: [],
            });
          }
          const group = grouped.get(name)!;
          if (acc.label && acc.label.trim() !== "") {
            group.label = acc.label;
          }
          group.total += acc.balance;
          group.amounts.push({
            balance: Math.abs(acc.balance),
            txIndex: acc.transactionIndex,
            isPositive: acc.isPositive,
          });
        });
        return Array.from(grouped.values());
      };

      const assetsGrouped = groupAccounts(displayData.assets);
      const liabilitiesGrouped = groupAccounts(displayData.liabilities);
      const equityGrouped = groupAccounts(displayData.equity);
      const expensesGrouped = groupAccounts(displayData.expenses);
      const incomeGrouped = groupAccounts(displayData.income);

      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Transaction Summary - All Transactions</title>
  <style>
    @font-face { font-family: 'Metropolis'; src: url('/assets/fonts/metropolis/Metropolis-Regular.otf') format('opentype'); font-weight: 400; font-style: normal; }
    @font-face { font-family: 'Metropolis'; src: url('/assets/fonts/metropolis/Metropolis-Medium.otf') format('opentype'); font-weight: 500; font-style: normal; }
    @font-face { font-family: 'Metropolis'; src: url('/assets/fonts/metropolis/Metropolis-SemiBold.otf') format('opentype'); font-weight: 600; font-style: normal; }
    @font-face { font-family: 'Metropolis'; src: url('/assets/fonts/metropolis/Metropolis-Bold.otf') format('opentype'); font-weight: 700; font-style: normal; }
    @font-face { font-family: 'Metropolis'; src: url('/assets/fonts/metropolis/Metropolis-ExtraBold.otf') format('opentype'); font-weight: 800; font-style: normal; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Metropolis', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: #f8f9fa;
      color: #333;
      line-height: 1.5;
    }
    .page {
      max-width: 850px;
      margin: 0 auto;
      background: white;
      padding: 40px;
      min-height: 100vh;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
    }
    .header h1 {
      font-size: 26px;
      font-weight: 700;
      color: #1a1a1a;
    }
    .header .subtitle {
      color: #888;
      font-size: 13px;
      margin-top: 4px;
    }
    .logo {
      height: 40px;
    }
    .logo img {
      height: 100%;
      width: auto;
    }
    .logo-text {
      font-size: 28px;
      font-weight: 800;
    }
    .logo-text .a, .logo-text .e { color: #0052a3; }
    .logo-text .m { color: #dc3545; }

    .section-title {
      font-size: 16px;
      font-weight: 700;
      color: #1a1a1a;
      margin: 30px 0 6px 0;
    }
    .section-subtitle {
      color: #999;
      font-size: 12px;
      margin-bottom: 12px;
    }

    .activity-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .column {
      border: 2px dashed;
      border-radius: 8px;
      padding: 8px;
    }
    .column.assets { border-color: #93c5fd; }
    .column.liabilities-equity { border-color: #fca5a5; }

    .category-box {
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 8px;
    }
    .category-box.blue { background: #eff6ff; }
    .category-box.red { background: #fef2f2; }

    .category-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .category-title {
      font-weight: 700;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .category-title.blue { color: #1e64b4; }
    .category-title.red { color: #b43232; }
    .info-icon {
      width: 16px;
      height: 16px;
      border: 1.5px solid;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 600;
    }
    .info-icon.blue { border-color: #1e64b4; color: #1e64b4; }
    .info-icon.red { border-color: #b43232; color: #b43232; }
    .category-total {
      font-weight: 700;
      font-size: 14px;
    }
    .category-total.blue { color: #1e64b4; }
    .category-total.red { color: #b43232; }

    .account-card {
      background: white;
      border: 1px solid #e5e5e5;
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 6px;
    }
    .account-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .account-name {
      font-weight: 600;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .account-name.blue { color: #1e64b4; }
    .account-name.red { color: #b43232; }
    .label-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      vertical-align: middle;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #f3f4f6;
      border: 1px solid #9ca3af;
      color: #6b7280;
      font-size: 9px;
      font-weight: 700;
      line-height: 1;
      margin-left: 4px;
      position: relative;
      top: -1px;
    }
    .drag-dots span {
      display: none;
    }
    .account-total {
      font-weight: 600;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .account-total.blue { color: #1e64b4; }
    .account-total.red { color: #b43232; }
    .chevron { display: none; }
    .drag-dots { display: none; }
    .info-icon { display: none; }

    .pills {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      position: relative;
      border: 1px solid;
    }
    .pill.blue {
      background: #dbeafe;
      border-color: #93c5fd;
      color: #1e64b4;
    }
    .pill.red {
      background: #fee2e2;
      border-color: #fca5a5;
      color: #b43232;
    }
    .pill::before {
      content: '';
      position: absolute;
      left: 0;
      top: 4px;
      bottom: 4px;
      width: 3px;
      border-radius: 2px;
    }
    .pill.blue::before { background: #1e64b4; }
    .pill.red::before { background: #b43232; }
    .pill sup {
      font-size: 9px;
      color: #888;
      margin-left: 4px;
      vertical-align: super;
    }

    .journal-section {
      margin-top: 40px;
    }
    .journal-container {
      background: white;
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .journal-title {
      padding: 16px 20px;
      font-weight: 700;
      font-size: 14px;
      border-bottom: 1px solid #eee;
    }
    .journal-table {
      width: 100%;
      border-collapse: collapse;
    }
    .journal-table th {
      background: #f8f8f8;
      padding: 10px 12px;
      text-align: left;
      font-size: 11px;
      font-weight: 600;
      color: #666;
      border-bottom: 1px solid #eee;
    }
    .journal-table td {
      padding: 6px 12px;
      font-size: 12px;
      vertical-align: top;
    }
    .journal-table tr.account-row-last td { border-bottom: 1px solid #f0f0f0; }
    .journal-table tr.account-row-last:last-child td { border-bottom: none; }
    .journal-table tr td.tx-num,
    .journal-table tr td.tx-date,
    .journal-table tr td.tx-details { padding-top: 12px; }
    .tx-num { font-weight: 700; color: #333; }
    .tx-date { color: #666; }
    .tx-details { color: #444; width: 240px; max-width: 240px; }
    .tx-notes { color: #999; font-size: 12px; margin-top: 4px; }
    .account-entry {
      margin-bottom: 6px;
      min-height: 1.2em;
      line-height: 1.2em;
    }
    .account-entry:last-child { margin-bottom: 0; }
    .account-link { color: #1e64b4; }
    .account-link.liability { color: #b43232; }
    .debit { color: #1e64b4; font-weight: 600; }
    .credit { color: #b43232; font-weight: 600; }

    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #e5e5e5;
      font-size: 12px;
      color: #888;
    }
    .footer-logo {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .footer-logo .logo { height: 24px; }
    .footer-logo .logo-text { font-size: 16px; }

    @media print {
      body { background: white; }
      .page { padding: 20px; max-width: none; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div>
        <h1>Transaction Summary</h1>
        <div class="subtitle">A detailed transaction summary and snapshot of your accounts${lastTxId > 0 ? ` (${totalTransactions} transaction${totalTransactions !== 1 ? "s" : ""})` : ""}.</div>
      </div>
      <div class="logo">
        <img src="/assets/images/logo/logo.png" alt="AME Logo" />
      </div>
    </div>

    <div class="section-title">Activity Area Snapshot Section</div>
    <div class="section-subtitle">Here is a snapshot of your Activity Area${lastTxId > 0 ? ` (up to transaction #${lastTxId})` : ""}</div>

    <div class="activity-grid">
      <div class="column assets">
        <div class="category-box blue">
          <div class="category-header">
            <div class="category-title blue">
              Assets <span class="info-icon blue">i</span>
            </div>
            <div class="category-total blue">${formatCurrency(totalAssets)}</div>
          </div>
          ${
            assetsGrouped.length === 0
              ? ''
              : assetsGrouped
                  .map(
                    (group) => `
            <div class="account-card">
              <div class="account-header">
                <div class="account-name blue">
                  <div class="drag-dots">
                    <span></span><span></span>
                    <span></span><span></span>
                    <span></span><span></span>
                  </div>
                  <span>${group.name}${group.label ? ` <span class="label-badge">${group.label.charAt(0).toUpperCase()}</span>` : ""}</span>
                </div>
                <div class="account-total blue">
                  ${formatCurrency(group.total)} <span class="chevron">&#9660;</span>
                </div>
              </div>
              <div class="pills">
                ${group.amounts
                  .map(
                    (amt) => `
                  <div class="pill ${amt.isPositive === false ? "red" : "blue"}">${formatCurrency(amt.balance)}<sup>${amt.txIndex}</sup></div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
        </div>
      </div>

      <div class="column liabilities-equity">
        <div class="category-box red">
          <div class="category-header">
            <div class="category-title red">
              Liabilities <span class="info-icon red">i</span>
            </div>
            <div class="category-total red">${formatCurrency(totalLiabilities)}</div>
          </div>
          ${
            liabilitiesGrouped.length === 0
              ? ''
              : liabilitiesGrouped
                  .map(
                    (group) => `
            <div class="account-card">
              <div class="account-header">
                <div class="account-name red">
                  <div class="drag-dots">
                    <span></span><span></span>
                    <span></span><span></span>
                    <span></span><span></span>
                  </div>
                  <span>${group.name}${group.label ? ` <span class="label-badge">${group.label.charAt(0).toUpperCase()}</span>` : ""}</span>
                </div>
                <div class="account-total red">
                  ${formatCurrency(group.total)} <span class="chevron">&#9660;</span>
                </div>
              </div>
              <div class="pills">
                ${group.amounts
                  .map(
                    (amt) => `
                  <div class="pill ${amt.isPositive === false ? "blue" : "red"}">${formatCurrency(amt.balance)}<sup>${amt.txIndex}</sup></div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
        </div>

        <div class="category-box red" style="margin-top: 8px;">
          <div class="category-header">
            <div class="category-title red">
              Equity <span class="info-icon red">i</span>
            </div>
            <div class="category-total red">${formatCurrency(totalEquity)}</div>
          </div>
          ${
            equityGrouped.length === 0
              ? ""
              : equityGrouped
                  .map(
                    (group) => `
            <div class="account-card">
              <div class="account-header">
                <div class="account-name red">
                  <div class="drag-dots">
                    <span></span><span></span>
                    <span></span><span></span>
                    <span></span><span></span>
                  </div>
                  <span>${group.name}${group.label ? ` <span class="label-badge">${group.label.charAt(0).toUpperCase()}</span>` : ""}</span>
                </div>
                <div class="account-total red">
                  ${formatCurrency(group.total)} <span class="chevron">&#9660;</span>
                </div>
              </div>
              <div class="pills">
                ${group.amounts
                  .map(
                    (amt) => `
                  <div class="pill ${amt.isPositive === false ? "blue" : "red"}">${formatCurrency(amt.balance)}<sup>${amt.txIndex}</sup></div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
        </div>
      </div>
    </div>

    ${
      configuredTransactions.some((t) => t.includeStatement === true)
        ? (() => {
            const _showLoss = totalIncome < totalExpenses && totalExpenses - totalIncome > 0;
            const _showProfit = totalIncome > totalExpenses;
            const _hasBanner = _showLoss || _showProfit;
            const _gridStyle = _hasBanner
              ? 'margin-top: 16px; grid-template-rows: auto minmax(40px, auto); align-items: start;'
              : 'margin-top: 16px;';
            const _expensesColStyle = _showLoss
              ? 'grid-row: span 2; align-self: stretch; display: flex; flex-direction: column;'
              : '';
            const _expensesBoxStyle = _showLoss
              ? 'flex: 1 1 auto; display: flex; flex-direction: column;'
              : '';
            const _incomeColStyle = _showProfit
              ? 'grid-row: span 2; align-self: stretch; display: flex; flex-direction: column;'
              : '';
            const _incomeBoxStyle = _showProfit
              ? 'flex: 1 1 auto; display: flex; flex-direction: column;'
              : '';
            return `
    <div class="activity-grid" style="${_gridStyle}">
      <div class="column assets" style="${_expensesColStyle}">
        <div class="category-box blue" style="${_expensesBoxStyle}">
          <div class="category-header">
            <div class="category-title blue">
              Expenses <span class="info-icon blue">i</span>
            </div>
            <div class="category-total blue">${formatCurrency(totalExpenses)}</div>
          </div>
          ${
            expensesGrouped.length === 0
              ? '<div style="color:#888;font-size:12px;padding:8px 0;">No expenses with balance</div>'
              : expensesGrouped
                  .map(
                    (group) => `
            <div class="account-card">
              <div class="account-header">
                <div class="account-name blue">
                  <div class="drag-dots">
                    <span></span><span></span>
                    <span></span><span></span>
                    <span></span><span></span>
                  </div>
                  <span>${group.name}${group.label ? ` <span class="label-badge">${group.label.charAt(0).toUpperCase()}</span>` : ""}</span>
                </div>
                <div class="account-total blue">
                  ${formatCurrency(group.total)} <span class="chevron">&#9660;</span>
                </div>
              </div>
              <div class="pills">
                ${group.amounts
                  .map(
                    (amt) => `
                  <div class="pill ${amt.isPositive === false ? "red" : "blue"}">${formatCurrency(amt.balance)}<sup>${amt.txIndex}</sup></div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
          ${
            totalIncome < totalExpenses && totalExpenses - totalIncome > 0
              ? `
          <div style="border-top:1px solid rgba(0,149,218,0.5);border-bottom:1px solid rgba(0,149,218,0.5);margin-top:auto;padding:8px;">
            <div style="display:flex;justify-content:space-between;align-items:center;color:#0369a1;font-weight:600;">
              <span>Loss</span>
              <span>${formatCurrency(totalExpenses - totalIncome)}</span>
            </div>
          </div>`
              : ""
          }
        </div>
      </div>

      <div class="column liabilities-equity" style="${_incomeColStyle}">
        <div class="category-box red" style="${_incomeBoxStyle}">
          <div class="category-header">
            <div class="category-title red">
              Income <span class="info-icon red">i</span>
            </div>
            <div class="category-total red">${formatCurrency(totalIncome)}</div>
          </div>
          ${
            incomeGrouped.length === 0
              ? '<div style="color:#888;font-size:12px;padding:8px 0;">No income with balance</div>'
              : incomeGrouped
                  .map(
                    (group) => `
            <div class="account-card">
              <div class="account-header">
                <div class="account-name red">
                  <div class="drag-dots">
                    <span></span><span></span>
                    <span></span><span></span>
                    <span></span><span></span>
                  </div>
                  <span>${group.name}${group.label ? ` <span class="label-badge">${group.label.charAt(0).toUpperCase()}</span>` : ""}</span>
                </div>
                <div class="account-total red">
                  ${formatCurrency(group.total)} <span class="chevron">&#9660;</span>
                </div>
              </div>
              <div class="pills">
                ${group.amounts
                  .map(
                    (amt) => `
                  <div class="pill ${amt.isPositive === false ? "blue" : "red"}">${formatCurrency(amt.balance)}<sup>${amt.txIndex}</sup></div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          `,
                  )
                  .join("")
          }
          ${
            totalIncome > totalExpenses
              ? `
          <div style="border-top:1px solid rgba(220,38,38,0.5);border-bottom:1px solid rgba(220,38,38,0.5);margin-top:auto;padding:8px;">
            <div style="display:flex;justify-content:space-between;align-items:center;color:#b91c1c;font-weight:600;">
              <span>Profit</span>
              <span>${formatCurrency(totalIncome - totalExpenses)}</span>
            </div>
          </div>`
              : ""
          }
        </div>
      </div>
    </div>
    `;
          })()
        : ""
    }

    ${(() => {
      const sectionDefs: Array<{section: string; hint: string}> = [];
      const sectionList: Array<{type: string; label: string}> = [
        { type: 'assets', label: 'Assets' },
        { type: 'liabilities', label: 'Liabilities' },
        { type: 'equity', label: 'Equity' },
        { type: 'expenses', label: 'Expenses' },
        { type: 'income', label: 'Income' },
      ];
      sectionList.forEach(({ type, label }) => {
        const hint = getSectionHint(type);
        if (hint && hint.trim() !== '') {
          sectionDefs.push({ section: label, hint: hint.trim() });
        }
      });

      const accountHints: Array<{section: string; name: string; comment: string}> = [];
      const addHints = (accounts: any[], section: string) => {
        (accounts || []).forEach((acc: any) => {
          if (acc.comment && acc.comment.trim() !== '') {
            if (!accountHints.some(h => h.section === section && h.name === (acc.name || section))) {
              accountHints.push({ section, name: acc.name || section, comment: acc.comment.trim() });
            }
          }
        });
      };
      addHints(liveAccounts || [], 'Assets');
      addHints(liveLiabilities || [], 'Liabilities');
      addHints(liveEquityAccounts || [], 'Equity');
      addHints(liveExpenseAccounts || [], 'Expenses');
      addHints(liveIncomeAccounts || [], 'Income');

      if (sectionDefs.length === 0 && accountHints.length === 0) return '';
      return `
    <div class="journal-section">
      <div class="section-title">Section Definitions</div>
      <div class="section-subtitle">Hints and definitions for sections and accounts</div>
      ${sectionDefs.length > 0 ? `
      <div style="margin-top:12px;font-family:inherit;">
        <div style="font-weight:600;font-size:13px;color:#374151;margin-bottom:6px;font-family:inherit;">Sections</div>
        ${sectionDefs.map(s => `
          <div style="margin-bottom:6px;padding:8px 12px;background:#ffffff;border:1px solid #e5e7eb;border-radius:6px;font-size:13px;font-family:inherit;">
            <span style="font-weight:600;color:#1a1a1a;">${s.section}</span>
            <span style="color:#666;"> — ${s.hint}</span>
          </div>
        `).join('')}
      </div>` : ''}
      ${accountHints.length > 0 ? `
      <div style="margin-top:12px;font-family:inherit;">
        <div style="font-weight:600;font-size:13px;color:#374151;margin-bottom:6px;font-family:inherit;">Accounts</div>
        ${accountHints.map(h => `
          <div style="margin-bottom:6px;padding:8px 12px;background:#ffffff;border:1px solid #e5e7eb;border-radius:6px;font-size:13px;font-family:inherit;">
            <span style="font-weight:600;color:#1a1a1a;">${h.section}: ${h.name}</span>
            <span style="color:#666;"> — ${h.comment}</span>
          </div>
        `).join('')}
      </div>` : ''}
    </div>`;
    })()}

    <div class="journal-section">
      <div class="section-title">Journal Entries</div>
      <div class="section-subtitle">Breakdown of accounts, debits, credits, and references for all transactions</div>

      <div class="journal-container">
        <div class="journal-title">${companyName || "Company's name"}'s Journal Entries</div>
        <table class="journal-table">
          <thead>
            <tr>
              <th style="width:40px">#</th>
              <th style="width:60px">Date</th>
              <th style="width:240px">Transaction Details</th>
              <th style="width:180px">Account</th>
              <th style="width:80px">Debit</th>
              <th style="width:80px">Credit</th>
            </tr>
          </thead>
          <tbody>
            ${allJournalEntries
              .map(
                (entry) =>
                  entry.accounts
                    .map((acc, accIndex) => {
                      const isLiabilitySide =
                        acc.type === "liability" ||
                        acc.type === "equity" ||
                        acc.type === "income";
                      const firstCells =
                        accIndex === 0
                          ? `
                <td class="tx-num" rowspan="${entry.accounts.length}">#${entry.transactionId}</td>
                <td class="tx-date" rowspan="${entry.accounts.length}">${formatTransactionDate(entry.date)}</td>
                <td class="tx-details" rowspan="${entry.accounts.length}">
                  ${entry.details || ""}
                  ${entry.notes ? `<div class="tx-notes">Notes: ${entry.notes}</div>` : ""}
                </td>`
                          : "";
                      const rowClass =
                        accIndex === entry.accounts.length - 1
                          ? ' class="account-row-last"'
                          : "";
                      return `
              <tr${rowClass}>
                ${firstCells}
                <td>
                  <span class="account-link ${isLiabilitySide ? "liability" : ""}">${acc.name || acc.type}</span>
                </td>
                <td>
                  ${acc.debit ? `<span class="debit">${formatCurrency(acc.debit)}</span>` : "&nbsp;"}
                </td>
                <td>
                  ${acc.credit ? `<span class="credit">${formatCurrency(acc.credit)}</span>` : "&nbsp;"}
                </td>
              </tr>
            `;
                    })
                    .join(""),
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>

    <div class="footer">
      <div class="footer-logo">
        <div class="logo">
          <img src="/assets/images/logo/logo.png" alt="AME Logo" />
        </div>
        <span>| Transaction Summary</span>
      </div>
    </div>
  </div>
</body>
</html>`;

      const name = companyName || "Company";
      openPrintableDocument({
        html,
        title: `Transaction Summary - ${name}`,
        fileName: `Transaction_Summary_${name.replace(/[^a-zA-Z0-9-_]+/g, "_")}_${
          new Date().toISOString().split("T")[0]
        }`,
      });
    } catch (error) {
      alert("Failed to generate export. Please try again.");
    }
    setIsExporting(false);
  };

  return { exportSummary, isExporting };
};

export default useTransactionSummaryExport;
